/* tslint:disable */
/* eslint-disable */
import { Injectable } from '@angular/core';
import { HttpClient, HttpResponse } from '@angular/common/http';
import { BaseService } from '../base-service';
import { ApiConfiguration } from '../api-configuration';
import { StrictHttpResponse } from '../strict-http-response';
import { RequestBuilder } from '../request-builder';
import { Observable } from 'rxjs';
import { map, filter } from 'rxjs/operators';

import { AiGenerateComponentFieldValueRequestDto } from '../models/ai-generate-component-field-value-request-dto';
import { AiGenerateComponentFieldValueResponseDto } from '../models/ai-generate-component-field-value-response-dto';
import { Component } from '../models/component';
import { ComponentPartial } from '../models/component-partial';
import { ComponentWithRelations } from '../models/component-with-relations';
import { NewComponent } from '../models/new-component';
import { OpenAiPageVariableUseAsComponentInputRequestDto } from '../models/open-ai-page-variable-use-as-component-input-request-dto';
import { OpenAiPageVariableUseAsComponentInputResponseDto } from '../models/open-ai-page-variable-use-as-component-input-response-dto';
import { Count as LoopbackCount } from '../models/loopback/count';

@Injectable({
  providedIn: 'root',
})
export class ComponentControllerService extends BaseService {
  constructor(
    config: ApiConfiguration,
    http: HttpClient
  ) {
    super(config, http);
  }

  /**
   * Path part for operation componentControllerCount
   */
  static readonly ComponentControllerCountPath = '/components/count';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `count()` instead.
   *
   * This method doesn't expect any request body.
   */
  count$Response(params?: {
    where?: any;
  }): Observable<StrictHttpResponse<LoopbackCount>> {

    const rb = new RequestBuilder(this.rootUrl, ComponentControllerService.ComponentControllerCountPath, 'get');
    if (params) {
      rb.query('where', params.where, {});
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json'
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<LoopbackCount>;
      })
    );
  }

  /**
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `count$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  count(params?: {
    where?: any;
  }): Observable<LoopbackCount> {

    return this.count$Response(params).pipe(
      map((r: StrictHttpResponse<LoopbackCount>) => r.body as LoopbackCount)
    );
  }

  /**
   * Path part for operation componentControllerGenerateByAi
   */
  static readonly ComponentControllerGenerateByAiPath = '/components/generate-field-value-by-ai';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `generateByAI()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  generateByAI$Response(params?: {
    body?: AiGenerateComponentFieldValueRequestDto
  }): Observable<StrictHttpResponse<AiGenerateComponentFieldValueResponseDto>> {

    const rb = new RequestBuilder(this.rootUrl, ComponentControllerService.ComponentControllerGenerateByAiPath, 'post');
    if (params) {
      rb.body(params.body, 'application/json');
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json'
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<AiGenerateComponentFieldValueResponseDto>;
      })
    );
  }

  /**
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `generateByAI$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  generateByAI(params?: {
    body?: AiGenerateComponentFieldValueRequestDto
  }): Observable<AiGenerateComponentFieldValueResponseDto> {

    return this.generateByAI$Response(params).pipe(
      map((r: StrictHttpResponse<AiGenerateComponentFieldValueResponseDto>) => r.body as AiGenerateComponentFieldValueResponseDto)
    );
  }

  /**
   * Path part for operation componentControllerGeneratePageVariableUseAsInput
   */
  static readonly ComponentControllerGeneratePageVariableUseAsInputPath = '/components/generate-page-variable-use-as-input-by-ai';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `generatePageVariableUseAsInput()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  generatePageVariableUseAsInput$Response(params?: {
    body?: OpenAiPageVariableUseAsComponentInputRequestDto
  }): Observable<StrictHttpResponse<OpenAiPageVariableUseAsComponentInputResponseDto>> {

    const rb = new RequestBuilder(this.rootUrl, ComponentControllerService.ComponentControllerGeneratePageVariableUseAsInputPath, 'post');
    if (params) {
      rb.body(params.body, 'application/json');
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json'
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<OpenAiPageVariableUseAsComponentInputResponseDto>;
      })
    );
  }

  /**
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `generatePageVariableUseAsInput$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  generatePageVariableUseAsInput(params?: {
    body?: OpenAiPageVariableUseAsComponentInputRequestDto
  }): Observable<OpenAiPageVariableUseAsComponentInputResponseDto> {

    return this.generatePageVariableUseAsInput$Response(params).pipe(
      map((r: StrictHttpResponse<OpenAiPageVariableUseAsComponentInputResponseDto>) => r.body as OpenAiPageVariableUseAsComponentInputResponseDto)
    );
  }

  /**
   * Path part for operation componentControllerUpdateInputs
   */
  static readonly ComponentControllerUpdateInputsPath = '/components/update-inputs/{id}';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `updateInputs()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateInputs$Response(params: {
    id: string;
    body?: { [key: string]: any }
  }): Observable<StrictHttpResponse<void>> {

    const rb = new RequestBuilder(this.rootUrl, ComponentControllerService.ComponentControllerUpdateInputsPath, 'patch');
    if (params) {
      rb.path('id', params.id, {});
      rb.body(params.body, 'application/json');
    }

    return this.http.request(rb.build({
      responseType: 'text',
      accept: '*/*'
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return (r as HttpResponse<any>).clone({ body: undefined }) as StrictHttpResponse<void>;
      })
    );
  }

  /**
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `updateInputs$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateInputs(params: {
    id: string;
    body?: { [key: string]: any }
  }): Observable<void> {

    return this.updateInputs$Response(params).pipe(
      map((r: StrictHttpResponse<void>) => r.body as void)
    );
  }

  /**
   * Path part for operation componentControllerFindById
   */
  static readonly ComponentControllerFindByIdPath = '/components/{id}';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `findById()` instead.
   *
   * This method doesn't expect any request body.
   */
  findById$Response(params: {
    id: string;
    filter?: any;
  }): Observable<StrictHttpResponse<ComponentWithRelations>> {

    const rb = new RequestBuilder(this.rootUrl, ComponentControllerService.ComponentControllerFindByIdPath, 'get');
    if (params) {
      rb.path('id', params.id, {});
      rb.query('filter', params.filter, {});
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json'
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<ComponentWithRelations>;
      })
    );
  }

  /**
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `findById$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  findById(params: {
    id: string;
    filter?: any;
  }): Observable<ComponentWithRelations> {

    return this.findById$Response(params).pipe(
      map((r: StrictHttpResponse<ComponentWithRelations>) => r.body as ComponentWithRelations)
    );
  }

  /**
   * Path part for operation componentControllerDeleteById
   */
  static readonly ComponentControllerDeleteByIdPath = '/components/{id}';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `deleteById()` instead.
   *
   * This method doesn't expect any request body.
   */
  deleteById$Response(params: {
    id: string;
  }): Observable<StrictHttpResponse<void>> {

    const rb = new RequestBuilder(this.rootUrl, ComponentControllerService.ComponentControllerDeleteByIdPath, 'delete');
    if (params) {
      rb.path('id', params.id, {});
    }

    return this.http.request(rb.build({
      responseType: 'text',
      accept: '*/*'
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return (r as HttpResponse<any>).clone({ body: undefined }) as StrictHttpResponse<void>;
      })
    );
  }

  /**
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `deleteById$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  deleteById(params: {
    id: string;
  }): Observable<void> {

    return this.deleteById$Response(params).pipe(
      map((r: StrictHttpResponse<void>) => r.body as void)
    );
  }

  /**
   * Path part for operation componentControllerUpdateById
   */
  static readonly ComponentControllerUpdateByIdPath = '/components/{id}';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `updateById()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateById$Response(params: {
    id: string;
    body?: ComponentPartial
  }): Observable<StrictHttpResponse<Component>> {

    const rb = new RequestBuilder(this.rootUrl, ComponentControllerService.ComponentControllerUpdateByIdPath, 'patch');
    if (params) {
      rb.path('id', params.id, {});
      rb.body(params.body, 'application/json');
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json'
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<Component>;
      })
    );
  }

  /**
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `updateById$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateById(params: {
    id: string;
    body?: ComponentPartial
  }): Observable<Component> {

    return this.updateById$Response(params).pipe(
      map((r: StrictHttpResponse<Component>) => r.body as Component)
    );
  }

  /**
   * Path part for operation componentControllerFind
   */
  static readonly ComponentControllerFindPath = '/components';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `find()` instead.
   *
   * This method doesn't expect any request body.
   */
  find$Response(params?: {
    filter?: any;
  }): Observable<StrictHttpResponse<Array<ComponentWithRelations>>> {

    const rb = new RequestBuilder(this.rootUrl, ComponentControllerService.ComponentControllerFindPath, 'get');
    if (params) {
      rb.query('filter', params.filter, {});
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json'
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<Array<ComponentWithRelations>>;
      })
    );
  }

  /**
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `find$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  find(params?: {
    filter?: any;
  }): Observable<Array<ComponentWithRelations>> {

    return this.find$Response(params).pipe(
      map((r: StrictHttpResponse<Array<ComponentWithRelations>>) => r.body as Array<ComponentWithRelations>)
    );
  }

  /**
   * Path part for operation componentControllerCreate
   */
  static readonly ComponentControllerCreatePath = '/components';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `create()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  create$Response(params?: {
    body?: NewComponent
  }): Observable<StrictHttpResponse<Component>> {

    const rb = new RequestBuilder(this.rootUrl, ComponentControllerService.ComponentControllerCreatePath, 'post');
    if (params) {
      rb.body(params.body, 'application/json');
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json'
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<Component>;
      })
    );
  }

  /**
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `create$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  create(params?: {
    body?: NewComponent
  }): Observable<Component> {

    return this.create$Response(params).pipe(
      map((r: StrictHttpResponse<Component>) => r.body as Component)
    );
  }

}
